import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Save, Upload, X, Sparkles, Loader2, Link } from 'lucide-react';
import { usePodcastEpisodeDialog } from '@/contexts/PodcastEpisodeDialogContext';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useCreatePodcastEpisode, useUpdatePodcastEpisode, usePodcastEpisodes } from '@/hooks/usePodcastEpisodes';
import { usePodcastGuests } from '@/hooks/usePodcastGuests';
import { EPISODE_STATUS_OPTIONS, PodcastEpisodeInsert } from '@/types/podcastHost';
import { RichTextEditor } from '@/components/learn/admin/RichTextEditor';
import { EpisodeGuestsManager } from './EpisodeGuestsManager';
import { PublishedLinksManager } from './PublishedLinksManager';
import { SocialMediaLinksManager } from './SocialMediaLinksManager';
import { DigcompCompetenceSelector } from '@/components/lms/digcomp/DigcompCompetenceSelector';
import { DigcompAIMapButton } from '@/components/lms/digcomp/DigcompAIMapButton';
import pentagonAllColors from '@/assets/digcomp/pentagon-all-colors.png';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { UnifiedLLMService } from '@/services/llm/UnifiedLLMService';
import { ApiCallsAuditService } from '@/services/apiCallsAuditService';

// Helper function to generate slug from title
const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

export const GlobalPodcastEpisodeDialog = () => {
  const { isOpen, mode, editingEpisode, showId, closeDialog } = usePodcastEpisodeDialog();
  const { currentBrandId } = useBrandManager();
  const createEpisode = useCreatePodcastEpisode();
  const updateEpisode = useUpdatePodcastEpisode();
  const { data: episodes = [] } = usePodcastEpisodes(currentBrandId, showId || undefined);
  const { data: guests = [] } = usePodcastGuests(currentBrandId);

  const [formData, setFormData] = useState<Partial<PodcastEpisodeInsert> & { 
    recording_link?: string; 
    invitation_recording_link?: string; 
    published_links?: Record<string, string>;
    social_media_links?: Record<string, string>;
    social_media_created?: boolean;
    digcomp_competence_id?: string | null;
    digcomp_area_id?: string | null;
    digcomp_level_id?: string | null;
  }>({
    title: '',
    slug: '',
    topic: '',
    description: '',
    target_record_date: '',
    target_publish_date: '',
    status: 'planned',
    thumbnail: '',
    guest_id: '',
    what_you_will_learn: '',
    story_behind: '',
    questions: '',
    recording_link: '',
    invitation_recording_link: '',
    published_links: {},
    social_media_links: {},
    social_media_created: false,
    digcomp_competence_id: null,
    digcomp_area_id: null,
    digcomp_level_id: null,
  });
  const [isUploading, setIsUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [descriptionSuggestions, setDescriptionSuggestions] = useState<string[]>([]);
  const [isGeneratingLearn, setIsGeneratingLearn] = useState(false);
  const [learnSuggestions, setLearnSuggestions] = useState<string[]>([]);
  const [isGeneratingStory, setIsGeneratingStory] = useState(false);
  const [storySuggestions, setStorySuggestions] = useState<string[]>([]);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [questionsSuggestions, setQuestionsSuggestions] = useState<string[]>([]);

  // Reset form when dialog opens/closes or mode changes
  useEffect(() => {
    if (isOpen) {
      setActiveTab('basic');
      if (mode === 'edit' && editingEpisode) {
        setFormData({
          title: editingEpisode.title,
          slug: (editingEpisode as any).slug || '',
          topic: editingEpisode.topic || '',
          description: editingEpisode.description || '',
          target_record_date: editingEpisode.target_record_date || '',
          target_publish_date: editingEpisode.target_publish_date || '',
          status: editingEpisode.status,
          thumbnail: (editingEpisode as any).thumbnail || '',
          guest_id: (editingEpisode as any).guest_id || '',
          what_you_will_learn: (editingEpisode as any).what_you_will_learn || '',
          story_behind: (editingEpisode as any).story_behind || '',
          questions: (editingEpisode as any).questions || '',
          recording_link: editingEpisode.recording_link || '',
          invitation_recording_link: (editingEpisode as any).invitation_recording_link || '',
          published_links: editingEpisode.published_links || {},
          social_media_links: (editingEpisode as any).social_media_links || {},
          social_media_created: (editingEpisode as any).social_media_created || false,
          digcomp_competence_id: editingEpisode.digcomp_competence_id ?? null,
          digcomp_area_id: editingEpisode.digcomp_area_id ?? null,
          digcomp_level_id: editingEpisode.digcomp_level_id ?? null,
        });
      } else {
        setFormData({
          title: '',
          slug: '',
          topic: '',
          description: '',
          target_record_date: '',
          target_publish_date: '',
          status: 'planned',
          thumbnail: '',
          guest_id: '',
          what_you_will_learn: '',
          story_behind: '',
          questions: '',
          recording_link: '',
          invitation_recording_link: '',
          published_links: {},
          social_media_links: {},
          social_media_created: false,
          digcomp_competence_id: null,
          digcomp_area_id: null,
          digcomp_level_id: null,
        });
      }
    }
  }, [isOpen, mode, editingEpisode]);

  // Auto-generate slug from title
  const handleTitleChange = (title: string) => {
    const newSlug = mode === 'create' || !formData.slug ? generateSlug(title) : formData.slug;
    setFormData({ ...formData, title, slug: newSlug });
  };

  // Handle thumbnail upload
  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentBrandId) return;

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${currentBrandId}/episodes/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('brand-assets')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('brand-assets')
        .getPublicUrl(fileName);

      setFormData({ ...formData, thumbnail: publicUrl });
      toast.success('Thumbnail uploaded');
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      toast.error('Failed to upload thumbnail');
    } finally {
      setIsUploading(false);
    }
  };

  const handleGenerateDescription = async () => {
    if (!formData.topic?.trim()) {
      toast.error('Please enter a topic first');
      return;
    }

    if (!currentBrandId) {
      toast.error('No brand selected');
      return;
    }

    setIsGeneratingDescription(true);
    setDescriptionSuggestions([]);
    ApiCallsAuditService.startTracking();

    try {
      const response = await UnifiedLLMService.callLLM({
        brandId: currentBrandId,
        source: 'podcast_episode_description',
        messages: [
          {
            role: 'system',
            content: 'You are an expert podcast producer and marketing copywriter. Generate exactly 3 unique, engaging episode descriptions for a podcast episode. Each description should be 3-5 paragraphs, compelling, and highlight the value for listeners. Return ONLY a JSON array with 3 HTML strings (use <p> tags for paragraphs, <strong> for emphasis), no additional text.'
          },
          {
            role: 'user',
            content: `Generate 3 different episode descriptions for a podcast episode about: "${formData.topic}". These will be used on the episode page. Make them engaging and informative. Return as a JSON array of 3 HTML strings.`
          }
        ],
        max_tokens: 2000
      });

      await ApiCallsAuditService.logApiCall({
        source_menu_item: 'podcast_episode_description',
        platform: (response.provider as any) || 'openai',
        brand_id: currentBrandId,
        model_used: response.model,
        status_code: 200,
        metadata: { topic: formData.topic }
      });

      const content = response.content.trim();
      let parsed: string[] = [];
      
      try {
        const jsonMatch = content.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          parsed = JSON.parse(jsonMatch[0]);
        } else {
          parsed = [content];
        }
      } catch {
        parsed = [content];
      }

      if (parsed.length > 0) {
        setDescriptionSuggestions(parsed.slice(0, 3));
        toast.success('Descriptions generated - click to select');
      }
    } catch (error) {
      console.error('Error generating description:', error);
      toast.error('Failed to generate descriptions');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleSelectDescriptionSuggestion = (suggestion: string) => {
    setFormData({ ...formData, description: suggestion });
    setDescriptionSuggestions([]);
  };

  const getContextForAI = () => {
    const topic = formData.topic?.trim() || '';
    const description = formData.description?.trim() || '';
    return `Topic: ${topic}\n\nDescription: ${description}`;
  };

  const handleGenerateLearn = async () => {
    if (!formData.topic?.trim()) {
      toast.error('Please enter a topic first');
      return;
    }
    if (!currentBrandId) {
      toast.error('No brand selected');
      return;
    }

    setIsGeneratingLearn(true);
    setLearnSuggestions([]);
    ApiCallsAuditService.startTracking();

    try {
      const response = await UnifiedLLMService.callLLM({
        brandId: currentBrandId,
        source: 'podcast_episode_learn',
        messages: [
          {
            role: 'system',
            content: 'You are an expert podcast producer. Generate exactly 3 unique "What You Will Learn" sections for a podcast episode. Each should be a bullet-point list of 4-6 key takeaways formatted in HTML (use <ul> and <li> tags). Return ONLY a JSON array with 3 HTML strings, no additional text.'
          },
          {
            role: 'user',
            content: `Generate 3 different "What You Will Learn" sections for this podcast episode:\n\n${getContextForAI()}\n\nReturn as a JSON array of 3 HTML strings with bullet points.`
          }
        ],
        max_tokens: 1500
      });

      await ApiCallsAuditService.logApiCall({
        source_menu_item: 'podcast_episode_learn',
        platform: (response.provider as any) || 'openai',
        brand_id: currentBrandId,
        model_used: response.model,
        status_code: 200,
        metadata: { topic: formData.topic }
      });

      const content = response.content.trim();
      let parsed: string[] = [];
      try {
        const jsonMatch = content.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          parsed = JSON.parse(jsonMatch[0]);
        } else {
          parsed = [content];
        }
      } catch {
        parsed = [content];
      }

      if (parsed.length > 0) {
        setLearnSuggestions(parsed.slice(0, 3));
        toast.success('Suggestions generated - click to select');
      }
    } catch (error) {
      console.error('Error generating learn section:', error);
      toast.error('Failed to generate suggestions');
    } finally {
      setIsGeneratingLearn(false);
    }
  };

  const handleSelectLearnSuggestion = (suggestion: string) => {
    setFormData({ ...formData, what_you_will_learn: suggestion });
    setLearnSuggestions([]);
  };

  const handleGenerateStory = async () => {
    if (!formData.topic?.trim()) {
      toast.error('Please enter a topic first');
      return;
    }
    if (!currentBrandId) {
      toast.error('No brand selected');
      return;
    }

    setIsGeneratingStory(true);
    setStorySuggestions([]);
    ApiCallsAuditService.startTracking();

    try {
      const response = await UnifiedLLMService.callLLM({
        brandId: currentBrandId,
        source: 'podcast_episode_story',
        messages: [
          {
            role: 'system',
            content: 'You are an expert podcast producer and storyteller. Generate exactly 3 unique "The Story Behind" sections for a podcast episode. Each should be 2-3 paragraphs telling the narrative behind the episode in an engaging way. Use HTML formatting (use <p> tags). Return ONLY a JSON array with 3 HTML strings, no additional text.'
          },
          {
            role: 'user',
            content: `Generate 3 different "The Story Behind" narratives for this podcast episode:\n\n${getContextForAI()}\n\nReturn as a JSON array of 3 HTML strings.`
          }
        ],
        max_tokens: 2000
      });

      await ApiCallsAuditService.logApiCall({
        source_menu_item: 'podcast_episode_story',
        platform: (response.provider as any) || 'openai',
        brand_id: currentBrandId,
        model_used: response.model,
        status_code: 200,
        metadata: { topic: formData.topic }
      });

      const content = response.content.trim();
      let parsed: string[] = [];
      try {
        const jsonMatch = content.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          parsed = JSON.parse(jsonMatch[0]);
        } else {
          parsed = [content];
        }
      } catch {
        parsed = [content];
      }

      if (parsed.length > 0) {
        setStorySuggestions(parsed.slice(0, 3));
        toast.success('Suggestions generated - click to select');
      }
    } catch (error) {
      console.error('Error generating story section:', error);
      toast.error('Failed to generate suggestions');
    } finally {
      setIsGeneratingStory(false);
    }
  };

  const handleSelectStorySuggestion = (suggestion: string) => {
    setFormData({ ...formData, story_behind: suggestion });
    setStorySuggestions([]);
  };

  const handleGenerateQuestions = async () => {
    if (!formData.topic?.trim()) {
      toast.error('Please enter a topic first');
      return;
    }
    if (!currentBrandId) {
      toast.error('No brand selected');
      return;
    }

    setIsGeneratingQuestions(true);
    setQuestionsSuggestions([]);
    ApiCallsAuditService.startTracking();

    try {
      const response = await UnifiedLLMService.callLLM({
        brandId: currentBrandId,
        source: 'podcast_episode_questions',
        messages: [
          {
            role: 'system',
            content: 'You are an expert podcast producer and interviewer. Generate exactly 3 unique "Questions" sections for a podcast episode. Each should be a numbered list of 5-8 thought-provoking interview questions. Use HTML formatting (use <ol> and <li> tags). Return ONLY a JSON array with 3 HTML strings, no additional text.'
          },
          {
            role: 'user',
            content: `Generate 3 different sets of interview questions for this podcast episode:\n\n${getContextForAI()}\n\nReturn as a JSON array of 3 HTML strings with numbered lists.`
          }
        ],
        max_tokens: 2000
      });

      await ApiCallsAuditService.logApiCall({
        source_menu_item: 'podcast_episode_questions',
        platform: (response.provider as any) || 'openai',
        brand_id: currentBrandId,
        model_used: response.model,
        status_code: 200,
        metadata: { topic: formData.topic }
      });

      const content = response.content.trim();
      let parsed: string[] = [];
      try {
        const jsonMatch = content.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          parsed = JSON.parse(jsonMatch[0]);
        } else {
          parsed = [content];
        }
      } catch {
        parsed = [content];
      }

      if (parsed.length > 0) {
        setQuestionsSuggestions(parsed.slice(0, 3));
        toast.success('Suggestions generated - click to select');
      }
    } catch (error) {
      console.error('Error generating questions:', error);
      toast.error('Failed to generate suggestions');
    } finally {
      setIsGeneratingQuestions(false);
    }
  };

  const handleSelectQuestionsSuggestion = (suggestion: string) => {
    setFormData({ ...formData, questions: suggestion });
    setQuestionsSuggestions([]);
  };

  const handleSubmit = async () => {
    if (!formData.title) return;

    // Auto-set social_media_created based on whether social media links exist
    const hasSocialMediaLinks = formData.social_media_links && Object.keys(formData.social_media_links).length > 0;

    // Clean up guest_id if empty string
    const submitData = {
      ...formData,
      guest_id: formData.guest_id || null,
      social_media_created: hasSocialMediaLinks,
    };

    if (mode === 'create') {
      if (!currentBrandId || !showId) return;
      const nextNumber = episodes.length + 1;
      await createEpisode.mutateAsync({
        ...submitData,
        brand_id: currentBrandId,
        show_id: showId,
        episode_number: nextNumber,
      } as PodcastEpisodeInsert);
    } else {
      if (!editingEpisode) return;
      await updateEpisode.mutateAsync({ id: editingEpisode.id, updates: submitData });

      // If social media links were added, update all episode guests to promotion-ready
      if (hasSocialMediaLinks && editingEpisode.id) {
        try {
          // Update episode_guests junction table
          await supabase
            .from('os_podcast_shows_episode_guests')
            .update({ status: 'promotion-ready' } as any)
            .eq('episode_id', editingEpisode.id);

          // Fetch all guest IDs linked to this episode
          const { data: episodeGuests } = await supabase
            .from('os_podcast_shows_episode_guests')
            .select('guest_id')
            .eq('episode_id', editingEpisode.id);

          // Collect all guest IDs (from junction + main guest_id)
          const guestIds = new Set<string>();
          if (submitData.guest_id) guestIds.add(submitData.guest_id);
          episodeGuests?.forEach((eg: any) => { if (eg.guest_id) guestIds.add(eg.guest_id); });

          // Update all guests' status
          if (guestIds.size > 0) {
            await supabase
              .from('os_podcast_shows_guests')
              .update({ status: 'promotion-ready' } as any)
              .in('id', Array.from(guestIds));
          }
        } catch (err) {
          console.error('Failed to update guest status:', err);
        }
      }
    }

    closeDialog();
  };

  const isPending = createEpisode.isPending || updateEpisode.isPending;
  const isCreate = mode === 'create';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitleWithBug 
            popupName={isCreate ? 'Create Episode' : 'Edit Episode'} 
            popupContext={`/podcasthost/shows/${showId}`}
            isOpen={isOpen}
          >
            {isCreate ? 'Create New Episode' : 'Edit Episode'}
          </DialogTitleWithBug>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="basic">Basic</TabsTrigger>
            <TabsTrigger value="guest">Guest</TabsTrigger>
            <TabsTrigger value="links">Links</TabsTrigger>
            <TabsTrigger value="technical">Technical</TabsTrigger>
            <TabsTrigger value="digcomp">DigComp</TabsTrigger>
            <TabsTrigger value="additional">Additional</TabsTrigger>
          </TabsList>

          {/* Basic Tab */}
          <TabsContent value="basic" className="space-y-4 mt-4">
            {/* Thumbnail Upload */}
            <div>
              <Label>Episode Thumbnail</Label>
              <div className="mt-2">
                {formData.thumbnail ? (
                  <div className="relative w-full max-w-xs">
                    <img 
                      src={formData.thumbnail} 
                      alt="Episode thumbnail" 
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 h-6 w-6"
                      onClick={() => setFormData({ ...formData, thumbnail: '' })}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full max-w-xs h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        {isUploading ? 'Uploading...' : 'Click to upload thumbnail'}
                      </p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleThumbnailUpload}
                      disabled={isUploading}
                    />
                  </label>
                )}
              </div>
            </div>

            <div>
              <Label>Episode Title *</Label>
              <Input
                value={formData.title || ''}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="Episode title"
              />
            </div>

            <div>
              <Label>Topic</Label>
              <Input
                value={formData.topic || ''}
                onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                placeholder="Main discussion topic"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Description</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerateDescription}
                  disabled={isGeneratingDescription || !formData.topic?.trim()}
                  className="h-7 px-2 text-xs"
                >
                  {isGeneratingDescription ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5 mr-1" />
                  )}
                  AI Suggest
                </Button>
              </div>
              
              {descriptionSuggestions.length > 0 && (
                <div className="mb-3 space-y-2">
                  <p className="text-xs text-muted-foreground">Click a suggestion to use it:</p>
                  {descriptionSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSelectDescriptionSuggestion(suggestion)}
                      className="w-full text-left p-3 rounded-lg border bg-muted/50 hover:bg-muted transition-colors text-sm prose prose-sm max-h-24 overflow-hidden"
                    >
                      <div 
                        className="line-clamp-3"
                        dangerouslySetInnerHTML={{ __html: suggestion }}
                      />
                    </button>
                  ))}
                </div>
              )}
              
              <RichTextEditor
                content={formData.description || ''}
                onChange={(content) => setFormData({ ...formData, description: content })}
                placeholder="Episode description..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Target Recording Date *</Label>
                <Input
                  type="date"
                  value={formData.target_record_date || ''}
                  onChange={(e) => setFormData({ ...formData, target_record_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Target Publish Date *</Label>
                <Input
                  type="date"
                  value={formData.target_publish_date || ''}
                  onChange={(e) => setFormData({ ...formData, target_publish_date: e.target.value })}
                  required
                />
              </div>
            </div>

            {!isCreate && (
              <div>
                <Label>Status</Label>
                <Select
                  value={formData.status || 'planned'}
                  onValueChange={(v) => setFormData({ ...formData, status: v as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EPISODE_STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </TabsContent>

          {/* Guest Tab */}
          <TabsContent value="guest" className="space-y-4 mt-4">
            <div>
              <Label>Main Guest</Label>
              <Select
                value={formData.guest_id || ''}
                onValueChange={(v) => setFormData({ ...formData, guest_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a guest (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No guest</SelectItem>
                  {guests.map((guest) => (
                    <SelectItem key={guest.id} value={guest.id}>
                      {guest.name} {guest.company ? `(${guest.company})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Additional Guests Manager - only in edit mode */}
            {!isCreate && editingEpisode && currentBrandId && (
              <EpisodeGuestsManager
                episodeId={editingEpisode.id}
                brandId={currentBrandId}
                mainGuestId={formData.guest_id || undefined}
              />
            )}
          </TabsContent>

          {/* Links Tab */}
          <TabsContent value="links" className="space-y-4 mt-4">
            <div>
              <Label>Invitation Recording Link</Label>
              <Input
                value={formData.invitation_recording_link || ''}
                onChange={(e) => setFormData({ ...formData, invitation_recording_link: e.target.value })}
                placeholder="https://calendly.com/..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                Link to send to guests for scheduling the recording
              </p>
            </div>

            <div>
              <Label>Recording Link</Label>
              <Input
                value={formData.recording_link || ''}
                onChange={(e) => setFormData({ ...formData, recording_link: e.target.value })}
                placeholder="https://riverside.fm/..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                Internal link to the recording session (Riverside, Zoom, etc.)
              </p>
            </div>

            <div>
              <Label>Published Links</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Platform URLs where this episode is published
              </p>
              <PublishedLinksManager
                value={formData.published_links || {}}
                onChange={(links) => setFormData({ ...formData, published_links: links })}
              />
            </div>

            <div>
              <Label>Social Media Links</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Share recording assets with guests (WeTransfer, Dropbox, Google Drive, etc.)
              </p>
              <SocialMediaLinksManager
                value={formData.social_media_links || {}}
                onChange={(links) => setFormData({ ...formData, social_media_links: links })}
              />
              {formData.social_media_created && (
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2 font-medium">✓ Social Media Created</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="technical" className="space-y-4 mt-4">
            <div>
              <Label>Slug</Label>
              <Input
                value={formData.slug || ''}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="episode-url-slug"
              />
              <p className="text-xs text-muted-foreground mt-1">
                URL-friendly identifier (auto-generated from title)
              </p>
            </div>
          </TabsContent>

          {/* DigComp Tab */}
          <TabsContent value="digcomp" className="space-y-4 mt-4">
            <div className="p-4 border rounded-lg bg-gradient-to-br from-primary/5 to-primary/10 space-y-4">
              <div className="flex items-center gap-2">
                <img src={pentagonAllColors} alt="DigComp" className="h-5 w-5" />
                <h3 className="text-lg font-semibold">DigComp 3.0 Competence</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Link this episode to a specific digital competence from the DigComp 3.0 framework
              </p>
              <div className="flex items-center gap-2">
                <DigcompAIMapButton
                  entityType="podcast"
                  entityId={editingEpisode?.id}
                  brandId={currentBrandId}
                  content={{
                    title: formData.title,
                    topic: formData.topic,
                    description: formData.description,
                    what_you_will_learn: formData.what_you_will_learn,
                    story_behind: formData.story_behind,
                  }}
                  onSuccess={async (result) => {
                    if (result && 'competence_id' in result) {
                      const updates = {
                        digcomp_competence_id: result.competence_id,
                        digcomp_area_id: result.area_id,
                        digcomp_level_id: result.level_id ?? null,
                      };
                      if (mode === 'edit' && editingEpisode) {
                        await updateEpisode.mutateAsync({
                          id: editingEpisode.id,
                          updates,
                        });
                      }
                      setFormData((prev) => ({ ...prev, ...updates }));
                    }
                  }}
                />
            </div>
              <DigcompCompetenceSelector
                brandId={currentBrandId}
                competenceId={formData.digcomp_competence_id ?? null}
                areaId={formData.digcomp_area_id ?? null}
                levelId={formData.digcomp_level_id ?? null}
                onCompetenceChange={(competenceId, areaId) => {
                  setFormData({
                    ...formData,
                    digcomp_competence_id: competenceId,
                    digcomp_area_id: areaId,
                  });
                }}
                onLevelChange={(levelId) => {
                  setFormData({ ...formData, digcomp_level_id: levelId });
                }}
                showLevelSelector
              />
            </div>
          </TabsContent>

          {/* Additional Tab */}
          <TabsContent value="additional" className="space-y-4 mt-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>What You Will Learn</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerateLearn}
                  disabled={isGeneratingLearn || !formData.topic?.trim()}
                  className="h-7 px-2 text-xs"
                >
                  {isGeneratingLearn ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5 mr-1" />
                  )}
                  AI Suggest
                </Button>
              </div>
              
              {learnSuggestions.length > 0 && (
                <div className="mb-3 space-y-2">
                  <p className="text-xs text-muted-foreground">Click a suggestion to use it:</p>
                  {learnSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSelectLearnSuggestion(suggestion)}
                      className="w-full text-left p-3 rounded-lg border bg-muted/50 hover:bg-muted transition-colors text-sm prose prose-sm max-h-24 overflow-hidden"
                    >
                      <div 
                        className="line-clamp-3"
                        dangerouslySetInnerHTML={{ __html: suggestion }}
                      />
                    </button>
                  ))}
                </div>
              )}
              
              <RichTextEditor
                content={formData.what_you_will_learn || ''}
                onChange={(content) => setFormData({ ...formData, what_you_will_learn: content })}
                placeholder="Key takeaways from this episode..."
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>The Story Behind</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerateStory}
                  disabled={isGeneratingStory || !formData.topic?.trim()}
                  className="h-7 px-2 text-xs"
                >
                  {isGeneratingStory ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5 mr-1" />
                  )}
                  AI Suggest
                </Button>
              </div>
              
              {storySuggestions.length > 0 && (
                <div className="mb-3 space-y-2">
                  <p className="text-xs text-muted-foreground">Click a suggestion to use it:</p>
                  {storySuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSelectStorySuggestion(suggestion)}
                      className="w-full text-left p-3 rounded-lg border bg-muted/50 hover:bg-muted transition-colors text-sm prose prose-sm max-h-24 overflow-hidden"
                    >
                      <div 
                        className="line-clamp-3"
                        dangerouslySetInnerHTML={{ __html: suggestion }}
                      />
                    </button>
                  ))}
                </div>
              )}
              
              <RichTextEditor
                content={formData.story_behind || ''}
                onChange={(content) => setFormData({ ...formData, story_behind: content })}
                placeholder="The story behind this episode..."
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Questions</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerateQuestions}
                  disabled={isGeneratingQuestions || !formData.topic?.trim()}
                  className="h-7 px-2 text-xs"
                >
                  {isGeneratingQuestions ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5 mr-1" />
                  )}
                  AI Suggest
                </Button>
              </div>
              
              {questionsSuggestions.length > 0 && (
                <div className="mb-3 space-y-2">
                  <p className="text-xs text-muted-foreground">Click a suggestion to use it:</p>
                  {questionsSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSelectQuestionsSuggestion(suggestion)}
                      className="w-full text-left p-3 rounded-lg border bg-muted/50 hover:bg-muted transition-colors text-sm prose prose-sm max-h-24 overflow-hidden"
                    >
                      <div 
                        className="line-clamp-3"
                        dangerouslySetInnerHTML={{ __html: suggestion }}
                      />
                    </button>
                  ))}
                </div>
              )}
              
              <RichTextEditor
                content={formData.questions || ''}
                onChange={(content) => setFormData({ ...formData, questions: content })}
                placeholder="Questions discussed in this episode..."
              />
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={closeDialog}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!formData.title || !formData.target_record_date || !formData.target_publish_date || isPending || isUploading}>
            <Save className="h-4 w-4 mr-2" />
            {isPending ? 'Saving...' : isCreate ? 'Create Episode' : 'Save Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
